import { useMemo, useState } from 'react';
import FormControl from '@material-ui/core/FormControl';
import InputLabel from '@material-ui/core/InputLabel';
import NativeSelect from '@material-ui/core/NativeSelect';

import { NewsType } from '../../../api/types/apiGetReport';

import FindingThread from './FindingThread';
import {
	DEFAULT_SORT,
	feedStatusLine,
	formatDateTime,
	monitoringEndedLine,
	resultCountLine,
	SORT_OPTIONS,
	WATCHDOG_COPY,
} from './watchdogCopy';
import { StatusChip } from './WatchdogUi';
import {
	buildThreads,
	categoryLabel,
	mergeNearDuplicates,
	SortKey,
	sortThreads,
	toFinding,
} from './watchdogUtils';

import styles from './Watchdog.module.scss';

type FindingsFeedType = {
	items: NewsType[];
	latestBatchCutoff: number | null;
	lastSweepAt: Date | null;
	nextSweepInHours: number;
	monitoringEnded: boolean;
	monitoringEndLabel: string;
};

export default function FindingsFeed(props: FindingsFeedType) {
	const {
		items,
		latestBatchCutoff,
		lastSweepAt,
		nextSweepInHours,
		monitoringEnded,
		monitoringEndLabel,
	} = props;

	const [query, setQuery] = useState('');
	const [sortBy, setSortBy] = useState<SortKey>(DEFAULT_SORT as SortKey);
	const [category, setCategory] = useState('');

	const findings = useMemo(
		() => mergeNearDuplicates(items.map((item) => toFinding(item, latestBatchCutoff))),
		[items, latestBatchCutoff],
	);

	const categories = useMemo(() => {
		const counts = new Map<string, number>();
		findings.forEach((finding) => {
			const key = finding.category || 'other';
			counts.set(key, (counts.get(key) || 0) + 1);
		});
		return [...counts.entries()]
			.map(([value, count]) => ({ value, count, label: categoryLabel(value) }))
			.sort((a, b) => a.label.localeCompare(b.label));
	}, [findings]);

	const filtered = useMemo(() => {
		const needle = query.trim().toLowerCase();
		return findings.filter((finding) => {
			if (category && (finding.category || 'other') !== category) {
				return false;
			}
			if (!needle) {
				return true;
			}
			return (
				finding.title.toLowerCase().includes(needle) ||
				finding.summary.toLowerCase().includes(needle) ||
				finding.body.toLowerCase().includes(needle) ||
				categoryLabel(finding.category).toLowerCase().includes(needle) ||
				finding.affectedAreas.some((area) => area.toLowerCase().includes(needle))
			);
		});
	}, [findings, query, category]);

	const threads = useMemo(() => sortThreads(buildThreads(filtered), sortBy), [filtered, sortBy]);

	const hasFilters = !!query.trim() || !!category;

	const clearAll = () => {
		setQuery('');
		setCategory('');
	};

	return (
		<section className={styles.feed}>
			<header className={styles['feed-head']}>
				<div className={styles['feed-title-row']}>
					<h3 className={styles['feed-title']}>{WATCHDOG_COPY.feedTitle}</h3>
					<StatusChip
						label={monitoringEnded ? WATCHDOG_COPY.endedChip : WATCHDOG_COPY.activeChip}
						tone={monitoringEnded ? 'ended' : 'active'}
					/>
				</div>
				<p className={styles['feed-status']}>
					{feedStatusLine(formatDateTime(lastSweepAt), nextSweepInHours)}
				</p>
				{monitoringEnded && monitoringEndLabel && (
					<p className={styles['feed-ended-note']}>{monitoringEndedLine(monitoringEndLabel)}</p>
				)}
			</header>

			<div className={styles.toolbar}>
				<div className={styles.search}>
					<span className={`material-icons ${styles['search-icon']}`}>search</span>
					<input
						name="search"
						type="text"
						className={styles['search-input']}
						placeholder={WATCHDOG_COPY.searchPlaceholder}
						value={query}
						onChange={(event) => setQuery(event.target.value)}
					/>
					{!!query && (
						<button
							type="button"
							className={styles['search-clear']}
							aria-label="Clear search"
							onClick={() => setQuery('')}
						>
							<span className="material-icons">close</span>
						</button>
					)}
				</div>

				<div className={styles.selects}>
					<FormControl>
						<InputLabel variant="standard" shrink disableAnimation htmlFor="findings-sort">
							{WATCHDOG_COPY.sortLabel}
						</InputLabel>
						<NativeSelect
							value={sortBy}
							inputProps={{ name: 'sort', id: 'findings-sort' }}
							onChange={(event) => setSortBy(event.target.value as SortKey)}
						>
							{SORT_OPTIONS.map((option) => (
								<option key={option.value} value={option.value}>
									{option.label}
								</option>
							))}
						</NativeSelect>
					</FormControl>

					<FormControl>
						<InputLabel variant="standard" shrink disableAnimation htmlFor="findings-category">
							{WATCHDOG_COPY.categoryLabel}
						</InputLabel>
						<NativeSelect
							value={category}
							inputProps={{ name: 'category', id: 'findings-category' }}
							onChange={(event) => setCategory(event.target.value)}
						>
							<option value="">{WATCHDOG_COPY.allCategories}</option>
							{categories.map((option) => (
								<option key={option.value} value={option.value}>
									{`${option.label} (${option.count})`}
								</option>
							))}
						</NativeSelect>
					</FormControl>
				</div>
			</div>

			<div className={styles['result-bar']}>
				<span>{resultCountLine(filtered.length, findings.length)}</span>
				{!!category && (
					<button type="button" className={styles['filter-chip']} onClick={() => setCategory('')}>
						{categoryLabel(category)}
						<span className="material-icons">close</span>
					</button>
				)}
				{!!query.trim() && (
					<button type="button" className={styles['filter-chip']} onClick={() => setQuery('')}>
						{`“${query.trim()}”`}
						<span className="material-icons">close</span>
					</button>
				)}
				{hasFilters && (
					<button type="button" className={styles['filter-chip']} onClick={clearAll}>
						{WATCHDOG_COPY.clearFilters}
					</button>
				)}
			</div>

			{filtered.length === 0 ? (
				<p className={styles['empty-search']}>{WATCHDOG_COPY.emptySearchResult}</p>
			) : (
				<div className={styles.findings}>
					{threads.map((thread) => (
						<FindingThread key={thread.key} thread={thread} />
					))}
				</div>
			)}
		</section>
	);
}
